// src/api/axiosBoot.js
//
// Side-effect module: installs request/response interceptors on BOTH the
// default axios instance AND window.fetch, so every component (including
// ones that call axios or fetch directly without going through
// services/http.js) automatically:
//
//   * sends the JWT from localStorage as `Authorization: Bearer <token>`
//     on same-origin /api/* requests
//   * on 401, clears the stale token and redirects to /login
//
// Imported once from src/index.js BEFORE App is rendered. Other axios
// instances (e.g. createClient(tenant) in src/api/client.js) are unaffected
// — they have their own interceptors installed in that file.

import axios from 'axios';

let _installed = false;

function isLoginUrl(url) {
  const s = String(url || '');
  return /\/api\/auth\/(login|register)/.test(s)
    || /\/api\/members\/[^/]+\/member-login/.test(s)
    || /\/api\/members\/[^/]+\/member-set-password/.test(s)
    || /\/api\/members\/[^/]+\/member-request-reset/.test(s);
}

function handle401Redirect() {
  if (typeof window === 'undefined') return;
  try { localStorage.removeItem('authToken'); } catch { /* ignore */ }
  const onLogin = window.location.pathname.startsWith('/login');
  const inPopup = window.opener && window.opener !== window;
  const inFrame = window.top !== window.self;
  if (!onLogin && !inPopup && !inFrame) {
    window.location.assign('/login');
  }
}

function isSameOriginApi(url) {
  if (typeof window === 'undefined') return false;
  const s = String(url || '');
  // Relative URLs starting with /api/ → same origin.
  if (s.startsWith('/api/')) return true;
  // Absolute URLs matching the current origin AND hitting /api/.
  try {
    const u = new URL(s, window.location.origin);
    return u.origin === window.location.origin && u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

export function installAxiosAuth() {
  if (_installed) return;
  _installed = true;

  axios.interceptors.request.use((config) => {
    try {
      const token = typeof localStorage !== 'undefined' && localStorage.getItem('authToken');
      if (token) {
        config.headers = config.headers || {};
        // Don't clobber an explicit override if a caller passed one.
        if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch { /* ignore */ }
    return config;
  });

  axios.interceptors.response.use(
    (resp) => resp,
    (err) => {
      const status = err?.response?.status;
      const url = err?.config?.url;
      if (status === 401 && !isLoginUrl(url)) handle401Redirect();
      return Promise.reject(err);
    }
  );

  installFetchAuth();
}

// --- window.fetch wrapper ------------------------------------------------
// Roughly half the codebase still uses bare fetch() for /api/* calls
// (scheduledPostsApi, inboxApi, analyticsApi, globalErrorHandler, several
// components). Wrap the global so they all pick up the JWT without
// requiring per-file rewrites.
function installFetchAuth() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  if (window.__onscreenFetchPatched__) return;
  window.__onscreenFetchPatched__ = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function patchedFetch(input, init) {
    let url;
    let request;
    if (typeof input === 'string') {
      url = input;
    } else if (input instanceof URL) {
      url = input.toString();
    } else if (input && typeof input === 'object' && 'url' in input) {
      // Request object
      request = input;
      url = input.url;
    }

    // Only inject for same-origin /api/* calls. External fetches (CDNs,
    // social media, etc.) get no header.
    if (isSameOriginApi(url)) {
      const token = (() => {
        try { return localStorage.getItem('authToken') || ''; } catch { return ''; }
      })();
      if (token) {
        // Build a new Headers object so we don't mutate the caller's input.
        const baseHeaders = new Headers(
          (init && init.headers) || (request && request.headers) || undefined
        );
        if (!baseHeaders.has('Authorization') && !baseHeaders.has('authorization')) {
          baseHeaders.set('Authorization', `Bearer ${token}`);
          init = { ...(init || {}), headers: baseHeaders };
        }
      }
    }

    let resp;
    try {
      resp = await originalFetch(input, init);
    } catch (err) {
      throw err;
    }
    if (resp && resp.status === 401 && !isLoginUrl(url)) handle401Redirect();
    return resp;
  };
}

// Auto-install on import so the file is a single import line in index.js.
installAxiosAuth();
